/**
 * Página pública de verificação do PDF da vistoria.
 * O SHA-256 é calculado localmente; o arquivo nunca é enviado.
 * @version 1.0.0
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, FileSearch, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type VerificationResult = { valid: boolean; report_version?: number; generated_at?: string; sha256?: string };

/** Calcula o SHA-256 sem transmitir o documento. */
async function sha256File(file: File) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export default function VerifyChecklistReportPage() {
  const [code, setCode] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const queryCode = new URLSearchParams(window.location.search).get('codigo');
    if (queryCode) setCode(queryCode.replace(/[^A-Za-z0-9-]/g, '').slice(0, 40).toUpperCase());
  }, []);

  /** Envia somente o código e o hash local à função protegida. */
  const verify = async () => {
    if (!file) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('checklist-report-verify', {
        body: { code: code.replace(/[^A-Za-z0-9-]/g, '').slice(0, 40), pdfSha256: await sha256File(file) },
      });
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);
      setResult(data as VerificationResult);
    } catch (caughtError) {
      setError((caughtError as Error).message || 'Não foi possível verificar agora.');
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 space-y-5 shadow-sm">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-lg font-semibold"><ShieldCheck className="h-5 w-5 text-primary" />Verificar vistoria</h1>
          <p className="text-xs text-muted-foreground">Informe o código do dossiê e selecione o PDF original. O arquivo é conferido no seu navegador e não é enviado.</p>
        </div>
        <div className="space-y-1.5"><Label>Código de verificação</Label><Input value={code} maxLength={30} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" onChange={(event) => setCode(event.target.value.toUpperCase())} /></div>
        <div className="space-y-1.5"><Label>Arquivo PDF</Label><Input type="file" accept="application/pdf" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></div>
        <Button className="w-full" disabled={loading || code.replace(/[^A-Z0-9]/g, '').length < 16 || !file} onClick={verify}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSearch className="mr-2 h-4 w-4" />}Verificar
        </Button>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {result && (result.valid ? (
          <div className="border-l-2 border-success pl-3 space-y-1 text-sm"><p className="flex items-center gap-1.5 font-semibold"><CheckCircle2 className="h-4 w-4 text-success" />Dossiê íntegro</p><p className="text-xs">Versão {result.report_version} · Gerado em {result.generated_at ? new Date(result.generated_at).toLocaleString('pt-BR') : '—'}</p><p className="break-all text-[10px] text-muted-foreground">SHA-256: {result.sha256}</p></div>
        ) : (
          <div className="border-l-2 border-destructive pl-3 text-sm"><p className="flex items-center gap-1.5 font-semibold"><XCircle className="h-4 w-4 text-destructive" />Não foi possível confirmar</p><p className="text-xs text-muted-foreground">O código é inválido ou o arquivo foi alterado.</p></div>
        ))}
      </div>
    </main>
  );
}